import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { useEffect, useState } from "react";
import { getItem } from "../../../../utils/localStorage";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import { UpdateBookingInvoicePaymentURL } from "../../../../utils/urls";

const RecordPaymentDialog = ({ visible, onHide, invoice, onPaymentSuccess }) => {
    const [submitting, setSubmitting] = useState(false);
    const [paymentData, setPaymentData] = useState({
        amount: 0,
        paymentMethod: '',
        receiptNumber: '',
        transactionId: '',
        notes: '',
        paymentDate: new Date()
    });

    const paymentMethods = [
        { label: 'Cash', value: 'Cash' },
        { label: 'Card', value: 'Card' },
        { label: 'Bank Transfer', value: 'Bank Transfer' },
        { label: 'Mobile Money', value: 'Mobile Money' },
        { label: 'Other', value: 'Other' }
    ];

    // Reset form when dialog opens with new invoice
    useEffect(() => {
        if (visible && invoice) {
            const balanceRemaining = invoice.calculatedData?.balanceRemaining || invoice.totalAmount || 0;
            // Generate receipt number automatically
            const receiptNum = `RCT-${Date.now().toString().slice(-8)}`;
            setPaymentData({
                amount: balanceRemaining > 0 ? balanceRemaining : 0,
                paymentMethod: '',
                receiptNumber: receiptNum,
                transactionId: '',
                notes: '',
                paymentDate: new Date()
            });
        }
    }, [visible, invoice]);

    const handleSubmit = async () => {
        if (!invoice) {
            toastify("No invoice selected", "error");
            return;
        }

        // Validation
        if (!paymentData.amount || paymentData.amount <= 0) {
            toastify("Please enter a valid payment amount", "error");
            return;
        }

        if (!paymentData.paymentMethod) {
            toastify("Please select a payment method", "error");
            return;
        }

        if (!paymentData.receiptNumber) {
            toastify("Please enter a receipt number", "error");
            return;
        }

        try {
            setSubmitting(true);
            const facilityId = await getItem("selectedFacilityId");

            const payload = {
                amount: paymentData.amount,
                paymentMethod: paymentData.paymentMethod,
                receiptNumber: paymentData.receiptNumber,
                transactionId: paymentData.transactionId,
                notes: paymentData.notes,
                paymentDate: paymentData.paymentDate.toISOString()
            };

            const response = await makeRequest2(
                `${UpdateBookingInvoicePaymentURL}/${facilityId}/${invoice._id}`,
                "PATCH",
                payload
            );

            if (response.success) {
                const { data } = response;
                const currencyCode = invoice.currencyDetails?.code || 'USD';

                toastify(
                    `Payment of ${currencyCode} ${paymentData.amount} recorded successfully. Pending approval in Finance Management.`,
                    "success"
                );

                // Show payment reference
                if (data.paymentReference) {
                    setTimeout(() => {
                        toastify(
                            `Payment Reference: ${data.paymentReference}`,
                            "info"
                        );
                    }, 1000);
                }

                // Call success callback to refresh parent data
                if (onPaymentSuccess && typeof onPaymentSuccess === 'function') {
                    onPaymentSuccess(data);
                }

                // Close dialog
                onHide();
            } else {
                toastify(response.error || "Failed to record payment", "error");
            }
        } catch (error) {
            console.error("Error recording payment:", error);
            toastify("An unexpected error occurred while recording payment", "error");
        } finally {
            setSubmitting(false);
        }
    };

    const formatAmount = (amount) => {
        if (amount === undefined || amount === null) return '0.00';
        const currencyCode = invoice?.currencyDetails?.code || 'USD';
        return `${currencyCode} ${parseFloat(amount).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    };

    const dialogFooter = (
        <div className="d-flex justify-content-end gap-2">
            <Button
                label="Cancel"
                icon="ti ti-x"
                className="p-button-text"
                onClick={onHide}
                disabled={submitting}
            />
            <Button
                label="Record Payment"
                icon="ti ti-check"
                className="p-button-primary"
                onClick={handleSubmit}
                loading={submitting}
            />
        </div>
    );

    if (!invoice) {
        return null;
    }

    const balanceRemaining = invoice.calculatedData?.balanceRemaining || invoice.totalAmount || 0;
    const totalAmount = invoice.totalAmount || 0;
    const totalPaid = invoice.calculatedData?.totalPaid || 0;

    return (
        <Dialog
            header={`Record Payment - Booking ${invoice.reservationDetails?.bookingReservationId || invoice.invoiceNumber}`}
            visible={visible}
            style={{ width: '600px', maxWidth: '95vw' }}
            onHide={onHide}
            footer={dialogFooter}
        >
            <div className="container-fluid p-0">
                {/* Invoice Summary */}
                <div className="card bg-light mb-4">
                    <div className="card-body">
                        <h5 className="card-title mb-3">Invoice Summary</h5>
                        <div className="row">
                            <div className="col-md-6 mb-2">
                                <small className="text-muted">Guest:</small>
                                <div className="fw-medium">{invoice.guestInfo?.name}</div>
                            </div>
                            <div className="col-md-6 mb-2">
                                <small className="text-muted">Apartment:</small>
                                <div className="fw-medium">{invoice.unitDetails?.name}</div>
                            </div>
                            <div className="col-md-6 mb-2">
                                <small className="text-muted">Total Amount:</small>
                                <div className="fw-medium">{formatAmount(totalAmount)}</div>
                            </div>
                            <div className="col-md-6 mb-2">
                                <small className="text-muted">Amount Paid:</small>
                                <div className="fw-medium text-success">{formatAmount(totalPaid)}</div>
                            </div>
                            <div className="col-12 mt-2">
                                <small className="text-muted">Balance Remaining:</small>
                                <div className={`fw-bold fs-5 ${balanceRemaining > 0 ? 'text-danger' : 'text-success'}`}>
                                    {formatAmount(balanceRemaining)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Payment Form */}
                <div className="mb-4">
                    <h5 className="mb-3">Payment Details</h5>

                    {/* Payment Amount */}
                    <div className="mb-3">
                        <label className="form-label">
                            Payment Amount <span className="text-danger">*</span>
                        </label>
                        <InputNumber
                            value={paymentData.amount}
                            onChange={(e) => setPaymentData({ ...paymentData, amount: e.value })}
                            mode="decimal"
                            minFractionDigits={2}
                            maxFractionDigits={2}
                            min={0}
                            max={balanceRemaining > 0 ? balanceRemaining * 2 : totalAmount * 2}
                            placeholder="0.00"
                            className="w-100"
                            prefix={invoice.currencyDetails?.code || 'USD'}
                        />
                        <small className="text-muted">
                            Maximum recommended: {formatAmount(balanceRemaining)}
                        </small>
                    </div>

                    {/* Payment Method */}
                    <div className="mb-3">
                        <label className="form-label">
                            Payment Method <span className="text-danger">*</span>
                        </label>
                        <Dropdown
                            value={paymentData.paymentMethod}
                            onChange={(e) => setPaymentData({ ...paymentData, paymentMethod: e.value })}
                            options={paymentMethods}
                            placeholder="Select payment method"
                            className="w-100"
                        />
                    </div>

                    {/* Receipt Number */}
                    <div className="mb-3">
                        <label className="form-label">
                            Receipt Number <span className="text-danger">*</span>
                        </label>
                        <InputText
                            value={paymentData.receiptNumber}
                            onChange={(e) => setPaymentData({ ...paymentData, receiptNumber: e.target.value })}
                            placeholder="Enter receipt number"
                            className="w-100"
                        />
                        <small className="text-muted">Auto-generated, but you can edit it</small>
                    </div>

                    {/* Transaction ID */}
                    <div className="mb-3">
                        <label className="form-label">
                            Transaction/Reference ID
                        </label>
                        <InputText
                            value={paymentData.transactionId}
                            onChange={(e) => setPaymentData({ ...paymentData, transactionId: e.target.value })}
                            placeholder="Enter transaction ID or reference number"
                            className="w-100"
                        />
                    </div>

                    {/* Payment Date */}
                    <div className="mb-3">
                        <label className="form-label">
                            Payment Date
                        </label>
                        <Calendar
                            value={paymentData.paymentDate}
                            onChange={(e) => setPaymentData({ ...paymentData, paymentDate: e.value })}
                            dateFormat="dd/mm/yy"
                            maxDate={new Date()}
                            showIcon
                            className="w-100"
                        />
                    </div>

                    {/* Notes */}
                    <div className="mb-3">
                        <label className="form-label">
                            Notes
                        </label>
                        <InputTextarea
                            value={paymentData.notes}
                            onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                            rows={3}
                            placeholder="Enter any additional notes about this payment"
                            className="w-100"
                        />
                    </div>
                </div>

                {/* Payment Preview */}
                {paymentData.amount > 0 && (
                    <div className="card border-primary">
                        <div className="card-header bg-primary text-white">
                            <h6 className="card-title mb-0">Payment Preview</h6>
                        </div>
                        <div className="card-body">
                            <div className="row g-2">
                                <div className="col-6">
                                    <small className="text-muted">Payment Amount:</small>
                                </div>
                                <div className="col-6 text-end">
                                    <small className="fw-medium">{formatAmount(paymentData.amount)}</small>
                                </div>
                                <div className="col-6">
                                    <small className="text-muted">New Total Paid:</small>
                                </div>
                                <div className="col-6 text-end">
                                    <small className="fw-medium">{formatAmount(totalPaid + paymentData.amount)}</small>
                                </div>
                                <div className="col-6">
                                    <small className="text-muted">Remaining Balance:</small>
                                </div>
                                <div className="col-6 text-end">
                                    <small className={`fw-medium ${(balanceRemaining - paymentData.amount) <= 0 ? 'text-success' : 'text-danger'}`}>
                                        {formatAmount(balanceRemaining - paymentData.amount)}
                                    </small>
                                </div>
                                <div className="col-6">
                                    <small className="text-muted">New Status:</small>
                                </div>
                                <div className="col-6 text-end">
                                    <small className="fw-medium">
                                        {(totalPaid + paymentData.amount) >= totalAmount ? 'Paid' :
                                            (totalPaid + paymentData.amount) > 0 ? 'Partially Paid' : 'Pending'}
                                    </small>
                                </div>
                                {(totalPaid + paymentData.amount) > totalAmount && (
                                    <>
                                        <div className="col-6">
                                            <small className="text-warning fw-medium">Overpayment:</small>
                                        </div>
                                        <div className="col-6 text-end">
                                            <small className="text-warning fw-medium">
                                                {formatAmount((totalPaid + paymentData.amount) - totalAmount)}
                                            </small>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Dialog>
    );
};

export default RecordPaymentDialog;